import { formatDataTest } from '@/common/dataTest/DataTest';
import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { EntityCard } from '../entityCard/EntityCard';
import { EntitySlotFilter } from './EntitySlotFilter';

/**
 * EntitySlot is a column for displaying EntityCards
 * It is responsible, for retrieving EntityObjects and rendering their EntityCards, retrieving FieldDefs for the entity,
 * applying filtering to the entity retrieval and refreshing the data
 */
export const EntitySlot = ({ slotSpec, dragStart, onEntitiesLoaded }) => {

  const theme = useTheme();
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  // Basics
  const [entityObjs, setEntityObjs] = useState(null);
  const [fieldToFieldDef, setFieldToFieldDef] = useState(null);

  // Filtering
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [updatedExpr, setUpdatedExpr] = useState(slotSpec.rowsReq.expr);
  const [filterApplied, setFilterApplied] = useState(false);

  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();
  useEffect(() => {
    subscrToEntityChange(module, slotSpec.entity);
    return () => {
      unsubscrToEntityChange(module, slotSpec.entity);
    };
  }, []);

  // whenever the parent’s rowsReq.expr changes, reset it
  useEffect(() => {
    setUpdatedExpr(slotSpec.rowsReq.expr);
  }, [slotSpec.rowsReq.expr]);

  useEffect(() => {
    rsrc.rows(module, slotSpec.entity, { ...slotSpec.rowsReq, expr: updatedExpr }).then(res => {
      const sorted = slotSpec.priority ? res.sort((a, b) => slotSpec.priority(b).index - slotSpec.priority(a).index) : res;
      setEntityObjs(sorted);
      onEntitiesLoaded(slotSpec.id, sorted);
    });
  }, [updatedExpr, refreshTrigger]);

  useEffect(() => {
    rsrc.fieldDefs(module, slotSpec.entity).then(res => {
      const fieldToFieldDef = Object.fromEntries(res.map(fieldDef => [fieldDef.field, fieldDef]));
      setFieldToFieldDef(fieldToFieldDef);
    });
  }, []);

  const isDisabled = !!dragStart && !(slotSpec.canDropFrom?.({fromSlotId: dragStart.fromSlotId, entityObj: dragStart.entityObj}));

  const slotBgColor = (isDraggingOver) => {
    if (isDisabled) return isDraggingOver ? 'error.light' : 'white';
    else return isDraggingOver ? 'grey.300' : 'grey.100';
  }

  const openFilter = (event) => {
    setIsFilterOpen(!isFilterOpen);
    setFilterAnchor(event.currentTarget);
  }

  const applyNewExpr = (newExpr) => {
    setIsFilterOpen(false);
    setUpdatedExpr(newExpr ? ExprUtils.and(newExpr, slotSpec.rowsReq.expr) : slotSpec.rowsReq.expr);
    setFilterApplied(newExpr != null);
  }

  if (!fieldToFieldDef) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 0, borderRadius: 2 }} >

      {/* Title and Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, position: 'relative' }}>
        <Typography align="center" fontWeight="normal" variant="subtitle1" >

          {/* Title */}
          {slotSpec.title}

          {/* Filter Button */}
          <IconButton sx={{ ml: 1, padding: '10px', bgcolor: filterApplied || isFilterOpen ? theme.palette.action.selected : 'transparent', }} size="small"
            onClick={openFilter}>
            <FontAwesomeIcon icon={fas.faBarsFilter} />
          </IconButton>

        </Typography>

        {/* Filter Component */}
        <EntitySlotFilter isOpen={isFilterOpen} applyNewExpr={applyNewExpr} cancel={() => setIsFilterOpen(false)}
          anchor={filterAnchor} fieldToFieldDef={fieldToFieldDef}
        />

      </Box>


      {/* Main Column */}
      <Droppable droppableId={slotSpec.id} isDropDisabled={isDisabled} >
        {(provided, snapshot) => {
          const { isDraggingOver } = snapshot;
          const bgColor = slotBgColor(isDraggingOver);
          const opacity = isDisabled ? 0.6 : 1;
          if (isDraggingOver) setIsFilterOpen(false);

          return (
            <Box 
            data-test-id={formatDataTest('board-slot', slotSpec.title)}
            ref={provided.innerRef} {...provided.droppableProps}
              sx={{
                flex: '1 1 auto', overflowY: 'auto',
                backgroundColor: bgColor, borderRadius: 4, opacity,
                transition: 'background-color 0.2s, opacity 0.2s', p: 1,
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {fieldToFieldDef && entityObjs?.map((entityObj, index) => (
                <Draggable key={entityObj.id} draggableId={`${slotSpec.id}|${entityObj.id}`} index={index} >
                  {(prov) => (
                    <Box ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} mb={1} >
                      <EntityCard entityObj={entityObj} slotSpec={slotSpec} fieldToFieldDef={fieldToFieldDef} index={index} />
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          );
        }}
      </Droppable>
    </Box>
  );
};
